import {
  OccurrenceOfficeReviewFields,
  OccurrenceReview,
  OfficeIrgReview,
  Priority,
  RelatedActivity,
  defaultOfficeReview,
  resolveSeedOffice,
  withRelatedActivityDefaults,
} from '../models/occurrence-review';

type RelatedActivitySeed = Omit<RelatedActivity, 'organisation' | 'office'>;

type OfficeIrgReviewSeed = Partial<Omit<OfficeIrgReview, 'office' | 'relatedActivities'>> & {
  office: string;
  relatedActivities?: RelatedActivitySeed[];
};

type FlatReviewSeed = Partial<Omit<OccurrenceOfficeReviewFields, 'relatedActivities'>> & {
  relatedActivities?: RelatedActivitySeed[];
};

type OccurrenceSeed = Omit<OccurrenceReview, 'irgReview' | 'escalatedToIrg'> &
  FlatReviewSeed & {
    escalatedToIrg?: boolean;
    irgReview?: OfficeIrgReviewSeed[];
    relatedActivities?: RelatedActivitySeed[];
  };

function isSeriousPriority(priority: Priority | null | undefined): boolean {
  return priority === 'Very High' || priority === 'High';
}

function irgReviewDefaults(
  seed: Partial<Omit<OfficeIrgReview, 'office' | 'relatedActivities'>>,
  _office: string,
  escalated: boolean,
): Pick<
  OfficeIrgReview,
  'seriousOccurrence' | 'irgComments' | 'leadRso' | 'irgStatus'
> {
  if (!escalated) {
    return {
      seriousOccurrence: false,
      irgComments: null,
      leadRso: null,
      irgStatus: null,
    };
  }

  const priority = seed.priority ?? null;
  return {
    irgStatus: seed.irgStatus ?? 'Open',
    leadRso: seed.leadRso ?? seed.rsoReviewedBy ?? null,
    irgComments: seed.irgComments ?? null,
    seriousOccurrence: seed.seriousOccurrence ?? isSeriousPriority(priority),
  };
}

function flatSeedToOfficeReview(seed: OccurrenceSeed, office: string): OfficeIrgReview {
  const escalated = seed.escalateToIrg === true || seed.escalatedToIrg === true;
  const defaults = defaultOfficeReview(office);
  const irgDefaults = irgReviewDefaults(
    {
      priority: seed.priority,
      rsoReviewedBy: seed.rsoReviewedBy,
      irgStatus: seed.irgStatus,
      leadRso: seed.leadRso,
      irgComments: seed.irgComments,
      seriousOccurrence: seed.seriousOccurrence,
    },
    office,
    escalated,
  );

  return {
    ...defaults,
    ...irgDefaults,
    office,
    rsoReview: seed.rsoReview ?? defaults.rsoReview,
    rsoReviewedBy: seed.rsoReviewedBy ?? defaults.rsoReviewedBy,
    rsoReviewDateTime: seed.rsoReviewDateTime ?? defaults.rsoReviewDateTime,
    rsoReviewNotes: seed.rsoReviewNotes ?? defaults.rsoReviewNotes,
    escalateToIrg: seed.escalateToIrg ?? defaults.escalateToIrg,
    escalateToIrgBy: seed.escalateToIrgBy ?? defaults.escalateToIrgBy,
    escalateToIrgDateTime: seed.escalateToIrgDateTime ?? defaults.escalateToIrgDateTime,
    escalateToIrgComments: seed.escalateToIrgComments ?? defaults.escalateToIrgComments,
    severity: seed.severity ?? defaults.severity,
    regulatoryValue: seed.regulatoryValue ?? defaults.regulatoryValue,
    priority: seed.priority ?? defaults.priority,
    prioritisedFollowUp: seed.prioritisedFollowUp ?? defaults.prioritisedFollowUp,
    relatedActivities: withRelatedActivityDefaults(
      { notifyingOrganisation: seed.notifyingOrganisation, state: seed.state, irgReview: [] },
      seed.relatedActivities ?? [],
      office,
    ),
  };
}

function buildIrgReview(seed: OccurrenceSeed): OfficeIrgReview[] {
  if (seed.irgReview?.length) {
    return seed.irgReview.map((reviewSeed) => {
      const office = reviewSeed.office;
      const base = defaultOfficeReview(office);
      const escalated = reviewSeed.escalateToIrg === true || seed.escalatedToIrg === true;
      return {
        ...base,
        ...irgReviewDefaults(
          {
            priority: reviewSeed.priority,
            rsoReviewedBy: reviewSeed.rsoReviewedBy,
            irgStatus: reviewSeed.irgStatus,
            leadRso: reviewSeed.leadRso,
            irgComments: reviewSeed.irgComments,
            seriousOccurrence: reviewSeed.seriousOccurrence,
          },
          office,
          escalated,
        ),
        ...reviewSeed,
        relatedActivities: withRelatedActivityDefaults(
          { notifyingOrganisation: seed.notifyingOrganisation, state: seed.state, irgReview: [] },
          reviewSeed.relatedActivities ?? [],
          office,
        ),
      };
    });
  }

  const office = resolveSeedOffice(seed);
  const hasFlatReviewData =
    seed.rsoReview ||
    seed.escalateToIrg ||
    seed.severity ||
    seed.priority ||
    seed.prioritisedFollowUp ||
    (seed.relatedActivities?.length ?? 0) > 0 ||
    seed.irgComments ||
    seed.leadRso;

  if (!hasFlatReviewData) {
    return [];
  }

  return [flatSeedToOfficeReview(seed, office)];
}

// Seed defaults: not yet reviewed, not escalated, not prioritised, no activities.
function occurrence(seed: OccurrenceSeed): OccurrenceReview {
  const { irgReview: _ignored, relatedActivities: _activities, ...coreSeed } = seed;

  return {
    escalatedToIrg: seed.escalatedToIrg ?? false,
    ...coreSeed,
    irgReview: buildIrgReview(seed),
  };
}

export interface GroupedOccurrenceReview {
  amalgamatedId: number;
  occurrences: OccurrenceReview[];
}

function buildGroupsByAmalgamatedId(reviews: OccurrenceReview[]): Map<number, GroupedOccurrenceReview> {
  const groups = new Map<number, GroupedOccurrenceReview>();

  for (const review of reviews) {
    const existing = groups.get(review.amalgamatedId);
    if (existing) {
      existing.occurrences.push(review);
      continue;
    }

    groups.set(review.amalgamatedId, {
      amalgamatedId: review.amalgamatedId,
      occurrences: [review],
    });
  }

  return groups;
}

export class GetOccurrenceReviews {
  static occurrenceReviews: OccurrenceReview[] = [
    occurrence({
      id: 1160427,
      amalgamatedId: 107610,
      operatorReference: 'OR-2026-04512',
      occurrenceDateTime: '2026-06-28T14:32:00',
      initialNotificationDateTime: '2026-06-28T15:05:00',
      finalSubmissionDateTime: '2026-07-02T09:41:00',
      finalReportDue: '2026-07-26',
      status: { label: 'Final' },
      category: 'A',
      state: 'SA',
      notifyingOrganisation: { id: 1, abbreviation: 'AO', name: 'Aurizon Operations' },
      rimOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations' },
      rsoOrganisation: { id: 1, abbreviation: 'AO', name: 'Aurizon Operations' },
      locationDescription: 'Islington Junction, Adelaide, SA',
      eventTypes: [{ label: 'Derailment'}, { label: 'Collision with infrastructure'}],
      description:
        'Loaded freight service derailed at Islington Junction after passing over a misaligned turnout, resulting in two wagons striking the adjacent signal gantry.',
      escalatedToIrg: true,
      escalateToIrg: true,
      relatedActivities: [
        { id: 1, reference: 'ACT-2026-0091', activityType: 'Investigation', status: 'In Progress', startDate: '2026-06-29' },
        { id: 2, reference: 'ACT-2026-0087', activityType: 'Compliance Inspection', status: 'Open', startDate: '2026-06-30' },
        { id: 3, reference: 'ACT-2025-0412', activityType: 'Audit', status: 'Closed', startDate: '2025-11-14' },
      ],
    }),
    occurrence({
      id: 1160428,
      amalgamatedId: 107611,
      operatorReference: 'OR-2026-04527',
      occurrenceDateTime: '2026-06-30T06:18:00',
      initialNotificationDateTime: '2026-06-30T07:02:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-07-28',
      status: { label: 'Awaiting Final Submission' },
      category: 'B',
      state: 'NSW',
      notifyingOrganisation: { id: 3, abbreviation: 'SYD', name: 'Sydney Trains' },
      rimOrganisation: { id: 3, abbreviation: 'SYD', name: 'Sydney Trains' },
      rsoOrganisation: { id: 3, abbreviation: 'SYD', name: 'Sydney Trains' },
      locationDescription: 'Strathfield Station, Platform 4, NSW',
      eventTypes: [{ label: 'Signal Passed at Danger (SPAD)'}],
      description:
        'Suburban passenger service passed signal SF-42 at danger by approximately 80 metres. No conflicting movements; train stopped by automatic train protection.',
      relatedActivities: [
        { id: 4, reference: 'ACT-2026-0102', activityType: 'Trend Analysis', status: 'Open', startDate: '2026-07-01' },
      ],
    }),
    occurrence({
      id: 1160429,
      amalgamatedId: 107612,
      operatorReference: 'OR-2026-04533',
      occurrenceDateTime: '2026-07-01T21:47:00',
      initialNotificationDateTime: '2026-07-01T22:20:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-07-29',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'VIC',
      notifyingOrganisation: { id: 4, abbreviation: 'MTM', name: 'Metro Trains Melbourne' },
      rimOrganisation: { id: 4, abbreviation: 'MTM', name: 'Metro Trains Melbourne' },
      rsoOrganisation: { id: 4, abbreviation: 'MTM', name: 'Metro Trains Melbourne' },
      locationDescription: 'Clifton Hill Loop, Track km 6.4, VIC',
      eventTypes: [{ label: 'Track and civil infrastructure irregularity'}],
      description:
        'Track patrol identified a broken rail on the up line within the Clifton Hill loop. Speed restriction applied and rail replaced overnight.',
    }),
    occurrence({
      id: 1160430,
      amalgamatedId: 107613,
      operatorReference: 'OR-2026-04549',
      occurrenceDateTime: '2026-07-02T11:09:00',
      initialNotificationDateTime: '2026-07-02T11:38:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-07-30',
      status: { label: 'Submitted' },
      category: 'A',
      state: 'QLD',
      notifyingOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      rimOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      rsoOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      locationDescription: 'Bowen Hills to Mayne, Track km 3.1, QLD',
      eventTypes: [{ label: 'Collision with person'}, { label: 'Emergency braking'}],
      description:
        'Passenger service struck a trespasser near the Mayne stabling yard access. Emergency services attended; the person sustained fatal injuries.',
      escalatedToIrg: true,
      escalateToIrg: true,
      relatedActivities: [
        { id: 5, reference: 'ACT-2026-0110', activityType: 'Investigation', status: 'In Progress', startDate: '2026-07-02' },
        { id: 6, reference: 'ACT-2026-0111', activityType: 'Site Inspection', status: 'Closed', startDate: '2026-07-03' },
      ],
    }),
    occurrence({
      id: 1160431,
      amalgamatedId: 107616,
      operatorReference: 'OR-2026-04555',
      occurrenceDateTime: '2026-07-02T16:52:00',
      initialNotificationDateTime: '2026-07-02T17:30:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-07-30',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'WA',
      notifyingOrganisation: { id: 6, abbreviation: 'AR', name: 'Arc Infrastructure' },
      rimOrganisation: { id: 6, abbreviation: 'AR', name: 'Arc Infrastructure' },
      rsoOrganisation: { id: 1, abbreviation: 'AO', name: 'Aurizon Operations'},
      locationDescription: 'Avon Yard, Northam, WA',
      eventTypes: [{ label: 'Rolling stock irregularity'}],
      description:
        "There are many variations of passages of Lorem Ipsum available, but the majority have suffered alteration in some form, by injected humour, or randomised words which don't look even slightly believable. If you are going to use a passage of Lorem Ipsum, you need to be sure there isn't anything embarrassing hidden in the middle of text. All the Lorem Ipsum generators on the Internet tend to repeat predefined chunks as necessary, making this the first true generator on the Internet. It uses a dictionary of over 200 Latin words, combined with a handful of model sentence structures, to generate Lorem Ipsum which looks reasonable. The generated Lorem Ipsum is therefore always free from repetition, injected humour, or non-characteristic words etc. There are many variations of passages of Lorem Ipsum available, but the majority have suffered alteration in some form, by injected humour, or randomised words which don't look even slightly believable. If you are going to use a passage of Lorem Ipsum, you need to be sure there isn't anything embarrassing hidden in the middle of text. All the Lorem Ipsum generators on the Internet tend to repeat predefined chunks as necessary, making this the first true generator on the Internet. It uses a dictionary of over 200 Latin words, combined with a handful of model sentence structures, to generate Lorem Ipsum which looks reasonable. The generated Lorem Ipsum is therefore always free from repetition, injected humour, or non-characteristic words etc.",
    }),
    occurrence({
      id: 1160432,
      amalgamatedId: 107617,
      operatorReference: 'OR-2026-04561',
      occurrenceDateTime: '2026-07-03T08:14:00',
      initialNotificationDateTime: '2026-07-03T08:55:00',
      finalSubmissionDateTime: '2026-07-04T13:22:00',
      finalReportDue: '2026-07-31',
      status: { label: 'Final' },
      category: 'B',
      state: 'NSW',
      notifyingOrganisation: { id: 7, abbreviation: 'PN', name: 'Pacific National' },
      rimOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations'},
      rsoOrganisation: { id: 7, abbreviation: 'PN', name: 'Pacific National' },
      locationDescription: 'Cootamundra West, Main South Line, Track km 428.7, NSW',
      eventTypes: [{ label: 'Level crossing occurrence'}, { label: 'Near miss'}],
      description:
        'Freight service sounded warning at an active level crossing as a road vehicle drove around lowered boom gates. No collision; CCTV footage secured.',
      relatedActivities: [
        { id: 7, reference: 'ACT-2026-0093', activityType: 'Level Crossing Review', status: 'Open', startDate: '2026-07-04' },
        { id: 8, reference: 'ACT-2026-0094', activityType: 'Trend Analysis', status: 'Open', startDate: '2026-07-04' },
        { id: 9, reference: 'ACT-2025-0388', activityType: 'Compliance Inspection', status: 'Closed', startDate: '2025-10-02' },
        { id: 10, reference: 'ACT-2025-0321', activityType: 'Audit', status: 'Closed', startDate: '2025-08-19' },
      ],
    }),
    occurrence({
      id: 1160433,
      amalgamatedId: 107618,
      operatorReference: 'OR-2026-04568',
      occurrenceDateTime: '2026-07-03T19:36:00',
      initialNotificationDateTime: '2026-07-03T20:10:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-07-31',
      status: { label: 'Awaiting Final Submission' },
      category: 'B',
      state: 'VIC',
      notifyingOrganisation: { id: 8, abbreviation: 'VL', name: 'V/Line' },
      rimOrganisation: { id: 8, abbreviation: 'VL', name: 'V/Line' },
      rsoOrganisation: { id: 8, abbreviation: 'VL', name: 'V/Line' },
      locationDescription: 'Ballarat Line, Bacchus Marsh, VIC',
      eventTypes: [{ label: 'Rolling stock fire'}],
      description:
        'Small electrical fire in the driver cab air-conditioning unit of a regional passenger service. Extinguished by the driver; service terminated at Bacchus Marsh.',
      relatedActivities: [
        { id: 11, reference: 'ACT-2026-0105', activityType: 'Fleet Review', status: 'Open', startDate: '2026-07-05' },
      ],
    }),
    occurrence({
      id: 1160434,
      amalgamatedId: 107622,
      operatorReference: 'OR-2026-04574',
      occurrenceDateTime: '2026-07-04T05:41:00',
      initialNotificationDateTime: '2026-07-04T06:12:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-01',
      status: { label: 'Submitted' },
      category: 'A',
      state: 'SA',
      notifyingOrganisation: { id: 9, abbreviation: 'KDA', name: 'Keolis Downer Adelaide' },
      rimOrganisation: { id: 10, abbreviation: 'DIT', name: 'Department for Infrastructure and Transport SA' },
      rsoOrganisation: { id: 9, abbreviation: 'KDA', name: 'Keolis Downer Adelaide' },
      locationDescription: 'Oaklands Crossing, Marion, SA',
      eventTypes: [{ label: 'Level crossing occurrence'}, { label: 'Collision with road vehicle'}],
      description:
        'Suburban passenger service collided with a road vehicle that entered the Oaklands crossing against active warnings. Driver of the vehicle seriously injured.',
      escalatedToIrg: true,
      escalateToIrg: true,
      relatedActivities: [
        { id: 12, reference: 'ACT-2026-0112', activityType: 'Investigation', status: 'In Progress', startDate: '2026-07-04' },
        { id: 13, reference: 'ACT-2026-0113', activityType: 'Site Inspection', status: 'Open', startDate: '2026-07-05' },
      ],
    }),
    occurrence({
      id: 1160435,
      amalgamatedId: 107623,
      operatorReference: 'OR-2026-04580',
      occurrenceDateTime: '2026-07-04T13:25:00',
      initialNotificationDateTime: '2026-07-04T14:02:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-01',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'QLD',
      notifyingOrganisation: { id: 17, abbreviation: 'AN', name: 'Aurizon Network' },
      rimOrganisation: { id: 17, abbreviation: 'AN', name: 'Aurizon Network' },
      rsoOrganisation: { id: 16, abbreviation: 'BHP', name: 'BHP Mitsubishi Alliance Rail' },
      locationDescription: 'Goonyella System, Coppabella, Track km 112.3, QLD',
      eventTypes: [{ label: 'Runaway rolling stock'}],
      description:
        'Three empty coal wagons rolled away during shunting at Coppabella yard, travelling 400 metres before stopping on the grade. No damage or injuries.',
      severity: 'Significant',
      regulatoryValue: 'Moderate',
      priority: 'High',
      prioritisedFollowUp: 'Refer to Intelligence Review Group.',
      relatedActivities: [
        { id: 14, reference: 'ACT-2026-0088', activityType: 'Trend Analysis', status: 'Open', startDate: '2026-07-05' },
      ],
    }),
    occurrence({
      id: 1160436,
      amalgamatedId: 107624,
      operatorReference: 'OR-2026-04586',
      occurrenceDateTime: '2026-07-05T02:58:00',
      initialNotificationDateTime: '2026-07-05T03:31:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-02',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'NSW',
      notifyingOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations' },
      rimOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations' },
      rsoOrganisation: { id: 18, abbreviation: 'QU', name: 'Qube Logistics' },
      locationDescription: 'Moss Vale, Main South Line, Track km 139.5, NSW',
      eventTypes: [{ label: 'Signalling and communications irregularity'}],
      description:
        'Signalling power supply failure between Moss Vale and Exeter caused all signals in the section to revert to stop. Services worked through under alternative safeworking.',
    }),
    occurrence({
      id: 1160437,
      amalgamatedId: 107625,
      operatorReference: 'OR-2026-04591',
      occurrenceDateTime: '2026-07-05T10:16:00',
      initialNotificationDateTime: '2026-07-05T10:44:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-02',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'TAS',
      notifyingOrganisation: { id: 12, abbreviation: 'TR', name: 'TasRail' },
      rimOrganisation: { id: 12, abbreviation: 'TR', name: 'TasRail' },
      rsoOrganisation: { id: 12, abbreviation: 'TR', name: 'TasRail' },
      locationDescription: 'Rhyndaston, South Line, Track km 63.8, TAS',
      eventTypes: [{ label: 'Track and civil infrastructure irregularity'}, { label: 'Landslip'}],
      description:
        'Minor landslip identified across the down line near Rhyndaston tunnel following heavy rainfall. Line closed pending geotechnical inspection.',
    }),
    occurrence({
      id: 1160438,
      amalgamatedId: 107626,
      operatorReference: 'OR-2026-04597',
      occurrenceDateTime: '2026-07-05T17:49:00',
      initialNotificationDateTime: '2026-07-05T18:27:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-02',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'NT',
      notifyingOrganisation: { id: 13, abbreviation: 'ORA', name: 'One Rail Australia' },
      rimOrganisation: { id: 13, abbreviation: 'ORA', name: 'One Rail Australia' },
      rsoOrganisation: { id: 13, abbreviation: 'ORA', name: 'One Rail Australia' },
      locationDescription: 'Katherine, North Australia Railway, Track km 271.0, NT',
      eventTypes: [{ label: 'Dangerous goods irregularity'}],
      description:
        'Minor leak detected from a container of corrosive substance during a scheduled inspection at Katherine terminal. Container isolated and made safe.',
      relatedActivities: [
        { id: 15, reference: 'ACT-2026-0107', activityType: 'Dangerous Goods Review', status: 'Open', startDate: '2026-07-06' },
      ],
    }),
    occurrence({
      id: 1160439,
      amalgamatedId: 107627,
      operatorReference: 'OR-2026-04603',
      occurrenceDateTime: '2026-07-06T07:03:00',
      initialNotificationDateTime: '2026-07-06T07:35:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-03',
      status: { label: 'Submitted' },
      category: 'A',
      state: 'NSW',
      notifyingOrganisation: { id: 14, abbreviation: 'NSW', name: 'NSW Trains' },
      rimOrganisation: { id: 15, abbreviation: 'TAH', name: 'Transport Asset Holding Entity' },
      rsoOrganisation: { id: 14, abbreviation: 'NSW', name: 'NSW Trains' },
      locationDescription: 'Hexham, Main North Line, Track km 172.4, NSW',
      eventTypes: [{ label: 'Collision between rolling stock'}, { label: 'Derailment'}],
      description:
        'Regional passenger service collided at low speed with a stabled track machine at Hexham. Lead car derailed; twelve passengers treated for minor injuries.',
      escalatedToIrg: true,
      escalateToIrg: true,
      relatedActivities: [
        { id: 16, reference: 'ACT-2026-0115', activityType: 'Investigation', status: 'In Progress', startDate: '2026-07-06' },
        { id: 17, reference: 'ACT-2026-0116', activityType: 'Site Inspection', status: 'Open', startDate: '2026-07-06' },
        { id: 18, reference: 'ACT-2026-0117', activityType: 'Safeworking Review', status: 'Open', startDate: '2026-07-06' },
      ],
    }),
    occurrence({
      id: 1160440,
      amalgamatedId: 107628,
      operatorReference: 'OR-2026-04609',
      occurrenceDateTime: '2026-07-06T09:57:00',
      initialNotificationDateTime: '2026-07-06T10:22:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-03',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'WA',
      notifyingOrganisation: { id: 11, abbreviation: 'PTA', name: 'Public Transport Authority WA' },
      rimOrganisation: { id: 11, abbreviation: 'PTA', name: 'Public Transport Authority WA' },
      rsoOrganisation: { id: 11, abbreviation: 'PTA', name: 'Public Transport Authority WA' },
      locationDescription: 'Perth Station, Platform 7, WA',
      eventTypes: [{ label: 'Slip, trip or fall'}, { label: 'Platform-train interface'}],
      description:
        'Passenger fell in the gap between the platform and train while boarding at Perth Station. Assisted by staff; taken to hospital with a leg injury.',
    }),
    occurrence({
      id: 1160441,
      amalgamatedId: 107630,
      operatorReference: 'OR-2026-04478',
      occurrenceDateTime: '2026-06-21T11:24:00',
      initialNotificationDateTime: '2026-06-21T12:01:00',
      finalSubmissionDateTime: '2026-06-25T15:12:00',
      finalReportDue: '2026-07-19',
      status: { label: 'Final' },
      category: 'A',
      state: 'SA',
      notifyingOrganisation: { id: 9, abbreviation: 'KDA', name: 'Keolis Downer Adelaide' },
      rimOrganisation: { id: 10, abbreviation: 'DIT', name: 'Department for Infrastructure and Transport SA' },
      rsoOrganisation: { id: 9, abbreviation: 'KDA', name: 'Keolis Downer Adelaide' },
      locationDescription: 'Goodwood Junction, Adelaide, SA',
      eventTypes: [{ label: 'Signal Passed at Danger (SPAD)'},{ label:  'Near miss'}],
      description:
        'Suburban passenger service passed signal GW-12 at danger and stopped 40 metres beyond, resulting in a near miss with a crossing freight movement.',
      escalatedToIrg: true,
      rsoReview: true,
      rsoReviewedBy: 'Emma Watson',
      rsoReviewOffice: 'Adelaide',
      rsoReviewDateTime: '2026-06-26T09:15:00',
      rsoReviewNotes: 'Reviewed with operator SPAD history. Escalation confirmed due to conflicting movement.',
      escalateToIrg: true,
      severity: 'Major',
      regulatoryValue: 'Moderate',
      priority: 'Very High',
      prioritisedFollowUp: 'Immediate escalation to Investigations.',
      relatedActivities: [
        { id: 19, reference: 'ACT-2026-0079', activityType: 'Investigation', status: 'In Progress', startDate: '2026-06-26' },
      ],
    }),
    occurrence({
      id: 1160442,
      amalgamatedId: 107631,
      operatorReference: 'OR-2026-04486',
      occurrenceDateTime: '2026-06-23T07:48:00',
      initialNotificationDateTime: '2026-06-23T08:20:00',
      finalSubmissionDateTime: '2026-06-27T10:33:00',
      finalReportDue: '2026-07-21',
      status: { label: 'Final' },
      category: 'B',
      state: 'NSW',
      notifyingOrganisation: { id: 7, abbreviation: 'PN', name: 'Pacific National' },
      rimOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations'},
      rsoOrganisation: { id: 7, abbreviation: 'PN', name: 'Pacific National' },
      locationDescription: 'Enfield Yard, Sydney, NSW',
      eventTypes: [{ label: 'Rolling stock irregularity'}],
      description:
        'Hot axle bearing detected by wayside equipment on a loaded container service approaching Enfield Yard. Wagon detached for inspection.',
      rsoReview: true,
      rsoReviewedBy: 'David Chen',
      rsoReviewOffice: 'Adelaide',
      rsoReviewDateTime: '2026-06-28T14:40:00',
      rsoReviewNotes: 'Detection systems worked as intended. No escalation required.',
      severity: 'Minor',
      regulatoryValue: 'Moderate',
      priority: 'Low',
      prioritisedFollowUp: 'No further action - active monitoring/trending in place to inform ORS.',
    }),
    occurrence({
      id: 1160443,
      amalgamatedId: 107632,
      operatorReference: 'OR-2026-04494',
      occurrenceDateTime: '2026-06-24T18:36:00',
      initialNotificationDateTime: '2026-06-24T19:05:00',
      finalSubmissionDateTime: '2026-06-29T11:47:00',
      finalReportDue: '2026-07-22',
      status: { label: 'Final' },
      category: 'B',
      state: 'VIC',
      notifyingOrganisation: { id: 4, abbreviation: 'MTM', name: 'Metro Trains Melbourne' },
      rimOrganisation: { id: 4, abbreviation: 'MTM', name: 'Metro Trains Melbourne' },
      rsoOrganisation: { id: 4, abbreviation: 'MTM', name: 'Metro Trains Melbourne' },
      locationDescription: 'Flinders Street Station, Platform 9, VIC',
      eventTypes: [{ label: 'Platform-train interface'}, { label: 'Slip, trip or fall'}],
      description:
        'Passenger caught a foot in the platform gap while alighting during evening peak. Station staff assisted; minor injuries reported.',
      irgReview: [
        {
          office: 'Adelaide',
          rsoReview: true,
          rsoReviewedBy: 'Ewald Eisele',
          rsoReviewDateTime: '2026-06-30T10:05:00',
          rsoReviewNotes: 'Initial review from Adelaide office.',
          escalateToIrg: true,
          escalateToIrgBy: 'Ewald Eisele',
          escalateToIrgDateTime: '2026-06-30T10:08:00',
          escalateToIrgComments: 'Recommend IRG review platform gap mitigation at high-traffic stations.',
          severity: 'Significant',
          regulatoryValue: 'Substantial',
          priority: 'High',
          prioritisedFollowUp: 'Consider targeted regulatory action/activity.',
          seriousOccurrence: true,
          irgStatus: 'Open',
          leadRso: 'Ewald Eisele',
          relatedActivities: [
            { id: 20, reference: 'ACT-2026-0083', activityType: 'Trend Analysis', status: 'Open', startDate: '2026-06-30' },
          ],
        },
        {
          office: 'Melbourne',
          rsoReview: true,
          rsoReviewedBy: 'Craig Wright',
          rsoReviewDateTime: '2026-07-01T09:20:00',
          rsoReviewNotes: 'Melbourne office review — local platform interface risk noted.',
          severity: 'Significant',
          regulatoryValue: 'Moderate',
          priority: 'Medium',
          prioritisedFollowUp: 'No further action - active monitoring/trending in place to inform ORS.',
        },
      ],
    }),
    occurrence({
      id: 1160444,
      amalgamatedId: 107610,
      operatorReference: 'OR-2026-04615',
      occurrenceDateTime: '2026-06-28T14:35:00',
      initialNotificationDateTime: '2026-06-28T15:10:00',
      finalSubmissionDateTime: '2026-07-03T11:20:00',
      finalReportDue: '2026-07-26',
      status: { label: 'Final' },
      category: 'A',
      state: 'SA',
      notifyingOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations'},
      rimOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations'},
      rsoOrganisation: { id: 1, abbreviation: 'AO', name: 'Aurizon Operations'},
      locationDescription: 'Islington Junction, Up Main, Track km 4.2, SA',
      eventTypes: [{ label: 'Signalling and communications irregularity'}],
      description: 'Signal maintenance crew reported intermittent track circuit failure on the up main approaching Islington Junction.',
      escalatedToIrg: true,
      escalateToIrg: true,
      relatedActivities: [
        { id: 22, reference: 'ACT-2026-0092', activityType: 'Investigation', status: 'In Progress', startDate: '2026-06-29' },
      ],
    }),
    occurrence({
      id: 1160445,
      amalgamatedId: 107610,
      operatorReference: 'OR-2026-04616',
      occurrenceDateTime: '2026-06-28T15:02:00',
      initialNotificationDateTime: '2026-06-28T15:38:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-07-26',
      status: { label: 'Awaiting Further Information' },
      category: 'A',
      state: 'SA',
      notifyingOrganisation: { id: 9, abbreviation: 'KDA', name: 'Keolis Downer Adelaide' },
      rimOrganisation: { id: 10, abbreviation: 'DIT', name: 'Department for Infrastructure and Transport SA' },
      rsoOrganisation: { id: 9, abbreviation: 'KDA', name: 'Keolis Downer Adelaide' },
      locationDescription: 'Islington Junction, Adelaide Metro Loop, SA',
      eventTypes: [{ label: 'Emergency braking'}, { label: 'Near miss'}],
      description:
        'Suburban service performed emergency brake application after observing debris on the track near the derailment site. Services suspended pending clearance. Investigation ongoing into whether debris originated from the primary derailment event or was pre-existing.',
      escalatedToIrg: true,
      escalateToIrg: true,
      relatedActivities: [
        { id: 23, reference: 'ACT-2026-0093', activityType: 'Site Inspection', status: 'Closed', startDate: '2026-06-29' },
        { id: 24, reference: 'ACT-2026-0094', activityType: 'Safeworking Review', status: 'Open', startDate: '2026-06-30' },
      ],
    }),
    occurrence({
      id: 1160446,
      amalgamatedId: 107610,
      operatorReference: 'OR-2026-04617',
      occurrenceDateTime: '2026-06-29T08:00:00',
      initialNotificationDateTime: '2026-06-29T08:45:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-07-27',
      status: { label: 'Submitted' },
      category: 'A',
      state: 'SA',
      notifyingOrganisation: { id: 18, abbreviation: 'QU', name: 'Qube Logistics' },
      rimOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations'},
      rsoOrganisation: { id: 18, abbreviation: 'QU', name: 'Qube Logistics' },
      locationDescription: 'Dry Creek North, Adelaide, SA',
      eventTypes: [{ label: 'Rolling stock irregularity'}],
      description: 'Wagon damage assessment following rerouting of freight services around Islington Junction closure.',
      escalatedToIrg: true,
    }),
    occurrence({
      id: 1160447,
      amalgamatedId: 107613,
      operatorReference: 'OR-2026-04618',
      occurrenceDateTime: '2026-07-02T11:15:00',
      initialNotificationDateTime: '2026-07-02T11:50:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-07-30',
      status: { label: 'Awaiting Final Submission' },
      category: 'A',
      state: 'QLD',
      notifyingOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      rimOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      rsoOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      locationDescription: 'Mayne Yard, Stabling Road 3, QLD',
      eventTypes: [{ label: 'Platform-train interface'}],
      description: 'Platform staff reported passenger distress on an adjacent platform during emergency response to the main line occurrence.',
      escalatedToIrg: true,
      escalateToIrg: true,
    }),
    occurrence({
      id: 1160448,
      amalgamatedId: 107613,
      operatorReference: 'OR-2026-04619',
      occurrenceDateTime: '2026-07-02T12:30:00',
      initialNotificationDateTime: '2026-07-02T13:05:00',
      finalSubmissionDateTime: '2026-07-05T16:00:00',
      finalReportDue: '2026-07-30',
      status: { label: 'Final' },
      category: 'A',
      state: 'QLD',
      notifyingOrganisation: { id: 17, abbreviation: 'AN', name: 'Aurizon Network' },
      rimOrganisation: { id: 17, abbreviation: 'AN', name: 'Aurizon Network' },
      rsoOrganisation: { id: 7, abbreviation: 'PN', name: 'Pacific National' },
      locationDescription: 'Bowen Hills, Main Line, Track km 2.8, QLD',
      eventTypes: [{ label: 'Signalling and communications irregularity'}],
      description:
        'Network control reported automatic route release failure affecting conflicting movements near the occurrence site. Signalling technicians restored normal operation within four hours. Full incident report documents signal timing logs, maintenance history for the interlocking, and corrective actions applied to prevent recurrence during concurrent emergency and recovery operations.',
      escalatedToIrg: true,
      escalateToIrg: true,
      relatedActivities: [
        { id: 25, reference: 'ACT-2026-0118', activityType: 'Investigation', status: 'In Progress', startDate: '2026-07-03' },
        { id: 26, reference: 'ACT-2026-0119', activityType: 'Compliance Inspection', status: 'Open', startDate: '2026-07-04' },
        { id: 27, reference: 'ACT-2026-0120', activityType: 'Trend Analysis', status: 'Open', startDate: '2026-07-05' },
      ],
    }),
    occurrence({
      id: 1160449,
      amalgamatedId: 107613,
      operatorReference: 'OR-2026-04620',
      occurrenceDateTime: '2026-07-02T18:00:00',
      initialNotificationDateTime: '2026-07-02T18:40:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-07-30',
      status: { label: 'Closed'},
      category: 'A',
      state: 'QLD',
      notifyingOrganisation: { id: 16, abbreviation: 'BHP', name: 'BHP Mitsubishi Alliance Rail' },
      rimOrganisation: { id: 17, abbreviation: 'AN', name: 'Aurizon Network' },
      rsoOrganisation: { id: 16, abbreviation: 'BHP', name: 'BHP Mitsubishi Alliance Rail' },
      locationDescription: 'Pinkenba Branch, QLD',
      eventTypes: [{ label: 'Rolling stock irregularity'}],
      description: 'Minor delay to coal service due to line closure upstream.',
      escalatedToIrg: true,
      relatedActivities: [
        { id: 28, reference: 'ACT-2026-0121', activityType: 'Enquiry', status: 'Closed', startDate: '2026-07-03' },
      ],
    }),
    occurrence({
      id: 1160450,
      amalgamatedId: 107628,
      operatorReference: 'OR-2026-04621',
      occurrenceDateTime: '2026-07-06T10:05:00',
      initialNotificationDateTime: '2026-07-06T10:40:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-03',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'WA',
      notifyingOrganisation: { id: 11, abbreviation: 'PTA', name: 'Public Transport Authority WA' },
      rimOrganisation: { id: 11, abbreviation: 'PTA', name: 'Public Transport Authority WA' },
      rsoOrganisation: { id: 11, abbreviation: 'PTA', name: 'Public Transport Authority WA' },
      locationDescription: 'Perth Station, Platform 5, WA',
      eventTypes: [{ label: 'Slip, trip or fall'}],
      description: 'Staff member slipped on wet platform surface during morning rain.',
    }),
    occurrence({
      id: 1160451,
      amalgamatedId: 107628,
      operatorReference: 'OR-2026-04622',
      occurrenceDateTime: '2026-07-06T10:30:00',
      initialNotificationDateTime: '2026-07-06T11:00:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-03',
      status: { label: 'Awaiting Final Submission' },
      category: 'B',
      state: 'WA',
      notifyingOrganisation: { id: 6, abbreviation: 'AR', name: 'Arc Infrastructure' },
      rimOrganisation: { id: 6, abbreviation: 'AR', name: 'Arc Infrastructure' },
      rsoOrganisation: { id: 7, abbreviation: 'PN', name: 'Pacific National' },
      locationDescription: 'Kewdale Freight Terminal, WA',
      eventTypes: [{ label: 'Dangerous goods irregularity'}],
      description:
        'During routine yard operations adjacent to Perth metropolitan passenger network, a container seal was found broken on a consignment labelled as non-dangerous goods. Subsequent inspection identified mis-declared limited quantity dangerous goods. Consignment quarantined and operator notified under chain of responsibility requirements.',
      relatedActivities: [
        { id: 29, reference: 'ACT-2026-0122', activityType: 'Dangerous Goods Review', status: 'Open', startDate: '2026-07-07' },
        { id: 30, reference: 'ACT-2026-0123', activityType: 'Audit', status: 'Open', startDate: '2026-07-08' },
      ],
    }),
    occurrence({
      id: 1160452,
      amalgamatedId: 107636,
      operatorReference: 'OR-2026-04623',
      occurrenceDateTime: '2026-07-07T06:22:00',
      initialNotificationDateTime: '2026-07-07T07:00:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-04',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'NSW',
      notifyingOrganisation: { id: 3, abbreviation: 'SYD', name: 'Sydney Trains' },
      rimOrganisation: { id: 3, abbreviation: 'SYD', name: 'Sydney Trains' },
      rsoOrganisation: { id: 3, abbreviation: 'SYD', name: 'Sydney Trains' },
      locationDescription: 'Redfern Station, Platform 12, NSW',
      eventTypes: [{ label: 'Signal Passed at Danger (SPAD)'}],
      description: 'Empty stock movement passed signal RF-18 at danger by approximately 30 metres during shunting.',
      relatedActivities: [
        { id: 31, reference: 'ACT-2026-0124', activityType: 'Trend Analysis', status: 'Open', startDate: '2026-07-08' },
      ],
    }),
    occurrence({
      id: 1160453,
      amalgamatedId: 107636,
      operatorReference: 'OR-2026-04624',
      occurrenceDateTime: '2026-07-07T06:25:00',
      initialNotificationDateTime: '2026-07-07T07:05:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-04',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'NSW',
      notifyingOrganisation: { id: 14, abbreviation: 'NSW', name: 'NSW Trains' },
      rimOrganisation: { id: 15, abbreviation: 'TAH', name: 'Transport Asset Holding Entity' },
      rsoOrganisation: { id: 14, abbreviation: 'NSW', name: 'NSW Trains' },
      locationDescription: 'Redfern Station, Up Main, NSW',
      eventTypes: [{ label: 'Near miss'}, { label: 'Signalling and communications irregularity'}],
      description: 'Conflicting route set for following passenger service cancelled by automatic interlocking after SPAD event on adjacent line.',
      relatedActivities: [
        { id: 32, reference: 'ACT-2026-0125', activityType: 'Investigation', status: 'In Progress', startDate: '2026-07-08' },
        { id: 33, reference: 'ACT-2026-0126', activityType: 'Safeworking Review', status: 'Open', startDate: '2026-07-08' },
        { id: 34, reference: 'ACT-2026-0127', activityType: 'Site Inspection', status: 'Closed', startDate: '2026-07-07' },
        { id: 35, reference: 'ACT-2025-0401', activityType: 'Compliance Inspection', status: 'Closed', startDate: '2025-12-01' },
      ],
    }),
    occurrence({
      id: 1160454,
      amalgamatedId: 107636,
      operatorReference: 'OR-2026-04625',
      occurrenceDateTime: '2026-07-07T07:10:00',
      initialNotificationDateTime: '2026-07-07T07:45:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-04',
      status: { label: 'Awaiting Further Information' },
      category: 'B',
      state: 'NSW',
      notifyingOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations'},
      rimOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations'},
      rsoOrganisation: { id: 18, abbreviation: 'QU', name: 'Qube Logistics' },
      locationDescription: 'Eveleigh Maintenance Centre, NSW',
      eventTypes: [{ label: 'Rolling stock irregularity'}],
      description: 'Wheel impact detected on freight wagon later identified as potentially related to track irregularity near Redfern.',
    }),
    occurrence({
      id: 1160455,
      amalgamatedId: 107636,
      operatorReference: 'OR-2026-04626',
      occurrenceDateTime: '2026-07-07T09:00:00',
      initialNotificationDateTime: '2026-07-07T09:30:00',
      finalSubmissionDateTime: '2026-07-09T14:15:00',
      finalReportDue: '2026-08-04',
      status: { label: 'Final' },
      category: 'B',
      state: 'NSW',
      notifyingOrganisation: { id: 7, abbreviation: 'PN', name: 'Pacific National' },
      rimOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations'},
      rsoOrganisation: { id: 7, abbreviation: 'PN', name: 'Pacific National' },
      locationDescription: 'Strathfield to Redfern, Main Suburban, NSW',
      eventTypes: [{ label: 'Track and civil infrastructure irregularity'}],
      description:
        'Track inspection following the Redfern SPAD cluster identified a minor rail head defect on the up suburban line. Defect marked and monitored pending planned renewal. This operator report consolidates track geometry measurements, ultrasonic test results, and train handling data from affected services to support the amalgamated occurrence assessment.',
      relatedActivities: [
        { id: 36, reference: 'ACT-2026-0128', activityType: 'Track Inspection', status: 'Closed', startDate: '2026-07-08' },
        { id: 37, reference: 'ACT-2026-0129', activityType: 'Trend Analysis', status: 'Open', startDate: '2026-07-09' },
      ],
    }),
    occurrence({
      id: 1160456,
      amalgamatedId: 107637,
      operatorReference: 'OR-2026-04627',
      occurrenceDateTime: '2026-07-08T13:40:00',
      initialNotificationDateTime: '2026-07-08T14:15:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-05',
      status: { label: 'Submitted' },
      category: 'A',
      state: 'VIC',
      notifyingOrganisation: { id: 8, abbreviation: 'VL', name: 'V/Line' },
      rimOrganisation: { id: 8, abbreviation: 'VL', name: 'V/Line' },
      rsoOrganisation: { id: 8, abbreviation: 'VL', name: 'V/Line' },
      locationDescription: 'Southern Cross Station, Platform 8, VIC',
      eventTypes: [{ label: 'Collision with person'}],
      description: 'Person entered the pit lane between platforms and was struck by a shunting locomotive at low speed.',
      escalatedToIrg: true,
      escalateToIrg: true,
      relatedActivities: [
        { id: 38, reference: 'ACT-2026-0130', activityType: 'Investigation', status: 'In Progress', startDate: '2026-07-08' },
        { id: 39, reference: 'ACT-2026-0131', activityType: 'Site Inspection', status: 'Open', startDate: '2026-07-08' },
        { id: 40, reference: 'ACT-2026-0132', activityType: 'Enquiry', status: 'Open', startDate: '2026-07-09' },
        { id: 41, reference: 'ACT-2026-0133', activityType: 'Safeworking Review', status: 'Open', startDate: '2026-07-09' },
        { id: 42, reference: 'ACT-2025-0377', activityType: 'Audit', status: 'Closed', startDate: '2025-09-22' },
      ],
    }),
    occurrence({
      id: 1160457,
      amalgamatedId: 107637,
      operatorReference: 'OR-2026-04628',
      occurrenceDateTime: '2026-07-08T14:00:00',
      initialNotificationDateTime: '2026-07-08T14:35:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-05',
      status: { label: 'Submitted' },
      category: 'A',
      state: 'VIC',
      notifyingOrganisation: { id: 4, abbreviation: 'MTM', name: 'Metro Trains Melbourne' },
      rimOrganisation: { id: 4, abbreviation: 'MTM', name: 'Metro Trains Melbourne' },
      rsoOrganisation: { id: 4, abbreviation: 'MTM', name: 'Metro Trains Melbourne' },
      locationDescription: 'Southern Cross Station, Collins Street concourse, VIC',
      eventTypes: [{ label: 'Slip, trip or fall'}],
      description: 'Passenger fall on escalator during crowd management following platform incident.',
      escalatedToIrg: true,
    }),
    occurrence({
      id: 1160458,
      amalgamatedId: 107637,
      operatorReference: 'OR-2026-04629',
      occurrenceDateTime: '2026-07-08T16:20:00',
      initialNotificationDateTime: '2026-07-08T16:55:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-05',
      status: { label: 'Awaiting Final Submission' },
      category: 'A',
      state: 'VIC',
      notifyingOrganisation: { id: 9, abbreviation: 'KDA', name: 'Keolis Downer Adelaide' },
      rimOrganisation: { id: 8, abbreviation: 'VL', name: 'V/Line' },
      rsoOrganisation: { id: 8, abbreviation: 'VL', name: 'V/Line' },
      locationDescription: 'Spencer Street Rail Bridge, VIC',
      eventTypes: [{ label: 'Emergency braking'}],
      description: 'Regional service emergency stopped after driver observed emergency services activity on adjacent tracks.',
      escalatedToIrg: true,
      escalateToIrg: true,
      relatedActivities: [
        { id: 43, reference: 'ACT-2026-0134', activityType: 'Investigation', status: 'Open', startDate: '2026-07-09' },
      ],
    }),
    occurrence({
      id: 1160459,
      amalgamatedId: 107638,
      operatorReference: 'OR-2026-04630',
      occurrenceDateTime: '2026-07-09T04:15:00',
      initialNotificationDateTime: '2026-07-09T04:50:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-06',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'QLD',
      notifyingOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      rimOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      rsoOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      locationDescription: 'Caboolture Line, Dakabin, QLD',
      eventTypes: [{ label: 'Level crossing occurrence'}],
      description: 'Vehicle stopped on level crossing; train halted short by automatic warning system.',
    }),
    occurrence({
      id: 1160460,
      amalgamatedId: 107638,
      operatorReference: 'OR-2026-04631',
      occurrenceDateTime: '2026-07-09T04:18:00',
      initialNotificationDateTime: '2026-07-09T05:00:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-06',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'QLD',
      notifyingOrganisation: { id: 1, abbreviation: 'AO', name: 'Aurizon Operations'},
      rimOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      rsoOrganisation: { id: 1, abbreviation: 'AO', name: 'Aurizon Operations'},
      locationDescription: 'North Coast Line, Dakabin, Track km 38.6, QLD',
      eventTypes: [{ label: 'Near miss'}, { label: 'Level crossing occurrence'}],
      description:
        'Following the Dakabin level crossing stoppage, a downstream freight service was held at caution under network control instructions. Investigation examines whether additional protection was required for conflicting freight and passenger movements during the level crossing closure and recovery phase.',
      relatedActivities: [
        { id: 44, reference: 'ACT-2026-0135', activityType: 'Level Crossing Review', status: 'Open', startDate: '2026-07-10' },
        { id: 45, reference: 'ACT-2026-0136', activityType: 'Trend Analysis', status: 'Open', startDate: '2026-07-10' },
        { id: 46, reference: 'ACT-2026-0137', activityType: 'Compliance Inspection', status: 'Open', startDate: '2026-07-11' },
      ],
    }),
    occurrence({
      id: 1160461,
      amalgamatedId: 107639,
      operatorReference: 'OR-2026-04632',
      occurrenceDateTime: '2026-07-10T08:30:00',
      initialNotificationDateTime: '2026-07-10T09:05:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-07',
      status: { label: 'Closed' },
      category: 'B',
      state: 'TAS',
      notifyingOrganisation: { id: 12, abbreviation: 'TR', name: 'TasRail' },
      rimOrganisation: { id: 12, abbreviation: 'TR', name: 'TasRail' },
      rsoOrganisation: { id: 12, abbreviation: 'TR', name: 'TasRail' },
      locationDescription: 'Derwent Valley Line, New Norfolk, TAS',
      eventTypes: [{ label: 'Rolling stock fire'}],
      description: 'Smouldering brake pad detected during run-round; train secured without injury.',
      rsoReview: true,
      rsoReviewedBy: 'David Chen',
      rsoReviewOffice: 'Adelaide',
      rsoReviewDateTime: '2026-07-12T11:00:00',
      rsoReviewNotes: 'Minor event. Closed after operator corrective action verified.',
      severity: 'Minor',
      regulatoryValue: 'Marginal',
      priority: 'Low',
      prioritisedFollowUp: 'No further action - active monitoring/trending in place to inform ORS.',
    }),
    occurrence({
      id: 1160462,
      amalgamatedId: 107640,
      operatorReference: 'OR-2026-04633',
      occurrenceDateTime: '2026-07-10T15:45:00',
      initialNotificationDateTime: '2026-07-10T16:20:00',
      finalSubmissionDateTime: '2026-07-12T10:00:00',
      finalReportDue: '2026-08-07',
      status: { label: 'Final' },
      category: 'B',
      state: 'SA',
      notifyingOrganisation: { id: 13, abbreviation: 'ORA', name: 'One Rail Australia' },
      rimOrganisation: { id: 13, abbreviation: 'ORA', name: 'One Rail Australia' },
      rsoOrganisation: { id: 13, abbreviation: 'ORA', name: 'One Rail Australia' },
      locationDescription: 'Port Augusta, Trans-Australian Railway, SA',
      eventTypes: [{ label: 'Runaway rolling stock'}],
      description: 'Single wagon rolled 120 metres during hump shunting before striking buffer stops.',
      relatedActivities: [
        { id: 47, reference: 'ACT-2026-0138', activityType: 'Fleet Review', status: 'Closed', startDate: '2026-07-11' },
      ],
    }),
    occurrence({
      id: 1160463,
      amalgamatedId: 107641,
      operatorReference: 'OR-2026-04634',
      occurrenceDateTime: '2026-07-11T22:10:00',
      initialNotificationDateTime: '2026-07-11T22:45:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-08',
      status: { label: 'Awaiting Final Submission' },
      category: 'B',
      state: 'NT',
      notifyingOrganisation: { id: 13, abbreviation: 'ORA', name: 'One Rail Australia' },
      rimOrganisation: { id: 13, abbreviation: 'ORA', name: 'One Rail Australia' },
      rsoOrganisation: { id: 13, abbreviation: 'ORA', name: 'One Rail Australia' },
      locationDescription: 'Alice Springs, North Australia Railway, NT',
      eventTypes: [{ label: 'Signalling and communications irregularity'}],
      description: 'Radio communications failure between train crew and network control for 18 minutes.',
    }),
    occurrence({
      id: 1160464,
      amalgamatedId: 107642,
      operatorReference: 'OR-2026-04635',
      occurrenceDateTime: '2026-06-15T09:00:00',
      initialNotificationDateTime: '2026-06-15T09:35:00',
      finalSubmissionDateTime: '2026-06-20T12:00:00',
      finalReportDue: '2026-07-13',
      status: { label: 'Final' },
      category: 'B',
      state: 'NSW',
      notifyingOrganisation: { id: 18, abbreviation: 'QU', name: 'Qube Logistics' },
      rimOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations'},
      rsoOrganisation: { id: 18, abbreviation: 'QU', name: 'Qube Logistics' },
      locationDescription: 'Junee Locomotive Depot, NSW',
      eventTypes: [{ label: 'Rolling stock irregularity'}],
      description:
        'During scheduled maintenance, technicians identified cracked bogie side frame on a grain hopper wagon. Wagon removed from service and fleet inspection initiated across similar class vehicles. Operator report includes metallurgical sampling outcomes, wagon build history, and interim fleet inspection statistics for the first 200 vehicles examined.',
      rsoReview: true,
      rsoReviewedBy: 'Emma Watson',
      rsoReviewOffice: 'Adelaide',
      rsoReviewDateTime: '2026-06-22T09:30:00',
      relatedActivities: [
        { id: 48, reference: 'ACT-2026-0065', activityType: 'Fleet Review', status: 'Closed', startDate: '2026-06-16' },
        { id: 49, reference: 'ACT-2026-0066', activityType: 'Audit', status: 'Closed', startDate: '2026-06-18' },
      ],
    }),
    occurrence({
      id: 1160465,
      amalgamatedId: 107643,
      operatorReference: 'OR-2026-04636',
      occurrenceDateTime: '2026-07-12T07:20:00',
      initialNotificationDateTime: '2026-07-12T07:55:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-09',
      status: { label: 'Submitted' },
      category: 'A',
      state: 'WA',
      notifyingOrganisation: { id: 16, abbreviation: 'BHP', name: 'BHP Mitsubishi Alliance Rail' },
      rimOrganisation: { id: 6, abbreviation: 'AR', name: 'Arc Infrastructure' },
      rsoOrganisation: { id: 16, abbreviation: 'BHP', name: 'BHP Mitsubishi Alliance Rail' },
      locationDescription: 'Newman, Hamersley Iron Line, WA',
      eventTypes: [{ label: 'Derailment'}],
      description: 'Empty ore wagon derailed on curve during inbound empty train movement.',
      escalatedToIrg: true,
      escalateToIrg: true,
      relatedActivities: [
        { id: 50, reference: 'ACT-2026-0139', activityType: 'Investigation', status: 'In Progress', startDate: '2026-07-12' },
        { id: 51, reference: 'ACT-2026-0140', activityType: 'Site Inspection', status: 'Open', startDate: '2026-07-13' },
      ],
    }),
    occurrence({
      id: 1160466,
      amalgamatedId: 107644,
      operatorReference: 'OR-2026-04637',
      occurrenceDateTime: '2026-07-12T12:00:00',
      initialNotificationDateTime: '2026-07-12T12:30:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-09',
      status: { label: 'Awaiting Further Information' },
      category: 'B',
      state: 'VIC',
      notifyingOrganisation: { id: 4, abbreviation: 'MTM', name: 'Metro Trains Melbourne' },
      rimOrganisation: { id: 4, abbreviation: 'MTM', name: 'Metro Trains Melbourne' },
      rsoOrganisation: { id: 4, abbreviation: 'MTM', name: 'Metro Trains Melbourne' },
      locationDescription: 'Richmond Station, Platform 3, VIC',
      eventTypes: [{ label: 'Platform-train interface'}],
      description: 'Door trap incident; passenger clothing caught in closing doors.',
    }),
    occurrence({
      id: 1160467,
      amalgamatedId: 107645,
      operatorReference: 'OR-2026-04638',
      occurrenceDateTime: '2026-07-13T05:50:00',
      initialNotificationDateTime: '2026-07-13T06:25:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-10',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'NSW',
      notifyingOrganisation: { id: 3, abbreviation: 'SYD', name: 'Sydney Trains' },
      rimOrganisation: { id: 3, abbreviation: 'SYD', name: 'Sydney Trains' },
      rsoOrganisation: { id: 3, abbreviation: 'SYD', name: 'Sydney Trains' },
      locationDescription: 'Hornsby Station, Platform 2, NSW',
      eventTypes: [{ label: 'Rolling stock irregularity'}],
      description: 'Passenger alarm activated due to air conditioning fault; train continued to next stop.',
      relatedActivities: [
        { id: 52, reference: 'ACT-2026-0141', activityType: 'Fleet Review', status: 'Open', startDate: '2026-07-14' },
        { id: 53, reference: 'ACT-2026-0142', activityType: 'Enquiry', status: 'Open', startDate: '2026-07-14' },
        { id: 54, reference: 'ACT-2026-0143', activityType: 'Trend Analysis', status: 'Open', startDate: '2026-07-15' },
        { id: 55, reference: 'ACT-2025-0399', activityType: 'Compliance Inspection', status: 'Closed', startDate: '2025-11-05' },
      ],
    }),
    occurrence({
      id: 1160468,
      amalgamatedId: 107646,
      operatorReference: 'OR-2026-04639',
      occurrenceDateTime: '2026-07-13T18:30:00',
      initialNotificationDateTime: '2026-07-13T19:00:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-10',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'QLD',
      notifyingOrganisation: { id: 7, abbreviation: 'PN', name: 'Pacific National' },
      rimOrganisation: { id: 17, abbreviation: 'AN', name: 'Aurizon Network' },
      rsoOrganisation: { id: 7, abbreviation: 'PN', name: 'Pacific National' },
      locationDescription: 'Acacia Ridge Yard, QLD',
      eventTypes: [{ label: 'Collision between rolling stock'}],
      description: 'Low-speed shunting collision between two wagon rakes; minor coupler damage only.',
    }),
    occurrence({
      id: 1160469,
      amalgamatedId: 107647,
      operatorReference: 'OR-2026-04640',
      occurrenceDateTime: '2026-07-14T03:00:00',
      initialNotificationDateTime: '2026-07-14T03:40:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-11',
      status: { label: 'Submitted' },
      category: 'A',
      state: 'NSW',
      notifyingOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations'},
      rimOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations'},
      rsoOrganisation: { id: 7, abbreviation: 'PN', name: 'Pacific National' },
      locationDescription: 'Macarthur to Campbelltown, Main South Line, NSW',
      eventTypes: [{ label: 'Track and civil infrastructure irregularity'}, { label: 'Landslip'}],
      description:
        'Geotechnical monitoring triggered alarm for slope movement above the down main line. Line closed and buses substituted while stabilisation works were designed. Report includes inclinometer readings, rainfall data for the preceding 72 hours, and interim slope remediation measures implemented before reopening under speed restriction.',
      escalatedToIrg: true,
      escalateToIrg: true,
      relatedActivities: [
        { id: 56, reference: 'ACT-2026-0144', activityType: 'Investigation', status: 'In Progress', startDate: '2026-07-14' },
        { id: 57, reference: 'ACT-2026-0145', activityType: 'Site Inspection', status: 'Open', startDate: '2026-07-14' },
        { id: 58, reference: 'ACT-2026-0146', activityType: 'Track Inspection', status: 'Open', startDate: '2026-07-15' },
        { id: 59, reference: 'ACT-2026-0147', activityType: 'Safeworking Review', status: 'Open', startDate: '2026-07-15' },
        { id: 60, reference: 'ACT-2025-0420', activityType: 'Audit', status: 'Closed', startDate: '2025-12-18' },
      ],
    }),
    occurrence({
      id: 1160470,
      amalgamatedId: 107648,
      operatorReference: 'OR-2026-04641',
      occurrenceDateTime: '2026-07-14T11:15:00',
      initialNotificationDateTime: '2026-07-14T11:50:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-11',
      status: { label: 'Awaiting Final Submission' },
      category: 'B',
      state: 'SA',
      notifyingOrganisation: { id: 1, abbreviation: 'AO', name: 'Aurizon Operations'},
      rimOrganisation: { id: 2, abbreviation: 'ART', name: 'Aurizon Operations'},
      rsoOrganisation: { id: 1, abbreviation: 'AO', name: 'Aurizon Operations'},
      locationDescription: 'Wolseley, Adelaide-Melbourne Line, SA',
      eventTypes: [{ label: 'Animal strike'}],
      description: 'Kangaroo strike on leading locomotive; no operational impact.',
    }),
    occurrence({
      id: 1160471,
      amalgamatedId: 107649,
      operatorReference: 'OR-2026-04642',
      occurrenceDateTime: '2026-07-15T09:30:00',
      initialNotificationDateTime: '2026-07-15T10:05:00',
      finalSubmissionDateTime: '2026-07-17T15:30:00',
      finalReportDue: '2026-08-12',
      status: { label: 'Final' },
      category: 'B',
      state: 'VIC',
      notifyingOrganisation: { id: 8, abbreviation: 'VL', name: 'V/Line' },
      rimOrganisation: { id: 8, abbreviation: 'VL', name: 'V/Line' },
      rsoOrganisation: { id: 8, abbreviation: 'VL', name: 'V/Line' },
      locationDescription: 'Geelong Line, North Geelong, VIC',
      eventTypes: [{ label: 'Signal Passed at Danger (SPAD)'}],
      description: 'Passenger service SPAD at North Geelong by 15 metres; no conflicting movement.',
      rsoReview: true,
      rsoReviewedBy: 'David Chen',
      rsoReviewOffice: 'Adelaide',
      rsoReviewDateTime: '2026-07-18T10:20:00',
      severity: 'Significant',
      regulatoryValue: 'Moderate',
      priority: 'Medium',
      prioritisedFollowUp: 'Consider targeted regulatory action/activity.',
      relatedActivities: [
        { id: 61, reference: 'ACT-2026-0148', activityType: 'Trend Analysis', status: 'Open', startDate: '2026-07-18' },
      ],
    }),
    occurrence({
      id: 1160472,
      amalgamatedId: 107653,
      operatorReference: 'OR-2026-04643',
      occurrenceDateTime: '2026-07-15T14:00:00',
      initialNotificationDateTime: '2026-07-15T14:35:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-12',
      status: { label: 'Submitted' },
      category: 'B',
      state: 'WA',
      notifyingOrganisation: { id: 11, abbreviation: 'PTA', name: 'Public Transport Authority WA' },
      rimOrganisation: { id: 11, abbreviation: 'PTA', name: 'Public Transport Authority WA' },
      rsoOrganisation: { id: 11, abbreviation: 'PTA', name: 'Public Transport Authority WA' },
      locationDescription: 'Mandurah Line, Murdoch Station, WA',
      eventTypes: [{ label: 'Emergency braking' }],
      description: 'Emergency brake after pedestrian entered fenced track area.',
    }),
    occurrence({
      id: 1160473,
      amalgamatedId: 107654,
      operatorReference: 'OR-2026-04644',
      occurrenceDateTime: '2026-07-16T06:45:00',
      initialNotificationDateTime: '2026-07-16T07:20:00',
      finalSubmissionDateTime: null,
      finalReportDue: '2026-08-13',
      status: { label: 'Submitted'},
      category: 'A',
      state: 'QLD',
      notifyingOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      rimOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      rsoOrganisation: { id: 5, abbreviation: 'QR', name: 'Queensland Rail' },
      locationDescription: 'Sunshine Coast Line, Beerburrum, QLD',
      eventTypes: [{ label: 'Collision with road vehicle' }, { label: 'Level crossing occurrence'}],
      description:
        'Passenger service collided with a road vehicle on a passive level crossing at Beerburrum. Vehicle driver sustained serious injuries and train sustained minor frontal damage. Emergency services response coordinated with Queensland Police. Preliminary findings note vegetation sighting obstruction and sun glare as contributing factors under investigation.',
      escalatedToIrg: true,
      escalateToIrg: true,
      relatedActivities: [
        { id: 62, reference: 'ACT-2026-0149', activityType: 'Investigation', status: 'In Progress', startDate: '2026-07-16' },
        { id: 63, reference: 'ACT-2026-0150', activityType: 'Level Crossing Review', status: 'Open', startDate: '2026-07-16' },
        { id: 64, reference: 'ACT-2026-0151', activityType: 'Site Inspection', status: 'Open', startDate: '2026-07-17' },
      ],
    }),
  ];
}

export class GetGroupedOccurrenceReviews {
  private static readonly groupsByAmalgamatedId = buildGroupsByAmalgamatedId(
    GetOccurrenceReviews.occurrenceReviews,
  );

  static byAmalgamatedId(amalgamatedId: number): GroupedOccurrenceReview | null {
    return GetGroupedOccurrenceReviews.groupsByAmalgamatedId.get(amalgamatedId) ?? null;
  }

  static all(): GroupedOccurrenceReview[] {
    return [...GetGroupedOccurrenceReviews.groupsByAmalgamatedId.values()];
  }
}
